import { getApiBaseUrl } from '../../../core/config/runtimeConfig';

export type SiteRecommendationAudience = 'RECRUITER' | 'JOB_SEEKER';

export interface PublishedRecommendation {
  id: string;
  fullName: string;
  currentPosition: string;
  companyName?: string | null;
  testimonial: string;
  audience?: SiteRecommendationAudience;
  publishedAt: string;
}

export interface RecommendationInvitationAccess {
  fullName: string;
  maskedEmail: string;
  audience?: SiteRecommendationAudience;
  expiresAt: string;
}

export interface SubmitRecommendationRequest {
  fullName: string;
  currentPosition: string;
  companyName?: string;
  testimonial: string;
  publicationConsent: boolean;
}

const responseError = async (response: Response): Promise<Error> => {
  try {
    const body = (await response.json()) as { message?: string };
    return new Error(body.message || `Request failed (${response.status})`);
  } catch {
    return new Error(`Request failed (${response.status})`);
  }
};

const jsonResponse = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) {
    throw new Error('The recommendation service is temporarily unavailable.');
  }
  return (await response.json()) as T;
};

export class SiteRecommendationService {
  static async published(): Promise<PublishedRecommendation[]> {
    const response = await fetch(
      `${getApiBaseUrl()}/public/site-recommendations/published`
    );
    if (!response.ok) throw await responseError(response);
    return jsonResponse<PublishedRecommendation[]>(response);
  }

  static async invitation(
    token: string
  ): Promise<RecommendationInvitationAccess> {
    const response = await fetch(
      `${getApiBaseUrl()}/public/site-recommendations/invitations/${encodeURIComponent(token)}`,
      { cache: 'no-store', referrerPolicy: 'no-referrer' }
    );
    if (!response.ok) throw await responseError(response);
    return jsonResponse<RecommendationInvitationAccess>(response);
  }

  static async submit(
    token: string,
    request: SubmitRecommendationRequest
  ): Promise<void> {
    const response = await fetch(
      `${getApiBaseUrl()}/public/site-recommendations/invitations/${encodeURIComponent(token)}/submit`,
      {
        method: 'POST',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      }
    );
    if (!response.ok) throw await responseError(response);
  }
}
